// src/pages/FinancialSummary.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import "./Styles/FinanceSumUI.css";
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faStore, faHashtag, faDownload,
    faEnvelope, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEyeSlash,
    faUsers, faUpload, faPlusCircle, faEye, faChartLine, faMoneyBillWave,
    faShoppingCart, faTruck, faWrench, faArrowCircleLeft, faUserCog,
    faWallet, faCoins, faProfit, faBalanceScale, faPhone, faMapMarkerAlt,
    faTools, faCogs, faUserFriends, faReceipt, faBoxes
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { setAuthData } from './mechanics_api.js';

// ============================================
// SHARED TRANSLATOR FOR FINANCIAL SUMMARY
// ============================================
const createFinancialTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_financial_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_financial_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Financial Summary': 'مالی خلاصہ',
                'Profit & Loss Statement': 'نفع و نقصان کا بیان',
                'All financial details are below': 'تمام مالی تفصیلات نیچے ہیں',
                'Date Range': 'تاریخ کی حد',
                'Start Date': 'شروع کی تاریخ',
                'End Date': 'آخری تاریخ',
                'Apply Filter': 'فلٹر لگائیں',
                'Reset': 'ری سیٹ کریں',
                'Product Sales Revenue': 'پروڈکٹ فروخت سے آمدنی',
                'Product Sales': 'پروڈکٹ کی فروخت',
                'Cost of Goods Sold': 'فروخت شدہ اشیاء کی قیمت',
                'Product Returns': 'پروڈکٹ کی واپسیاں',
                'Service Revenue': 'خدمات سے آمدنی',
                'Service Employee Payments': 'خدمات کے ملازمین کی تنخواہ',
                'Service Profit': 'سروس کا نفع',
                'Mobchange Revenue': 'موبائل تبدیلی سے آمدنی',
                'Mobchange Parts Cost': 'موبائل تبدیلی میں استعمال ہونے والے پرزہ جات',
                'Mobchange Profit': 'موبائل تبدیلی کا نفع',
                'General Expenses': 'عام اخراجات',
                'Employee Salaries': 'ملازمین کی تنخواہیں',
                'Gross Profit': 'مجموعی نفع',
                'Operating Profit': 'آپریٹنگ نفع',
                'Net Profit': 'خالص نفع',
                'Revenue': 'آمدنی',
                'Operating Expenses': 'آپریٹنگ اخراجات',
                'Sales': 'فروخت',
                'Purchases': 'خریداری',
                'Services': 'خدمات',
                'Mobchange': 'موبائل تبدیلی',
                'Returns': 'واپسیاں',
                'Salaries': 'تنخواہیں',
                'Expenses': 'اخراجات',
                'Total': 'کل',
                'Search...': 'تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'Filter': 'فلٹر',
                'Filters': 'فلٹرز',
                'Show Filters': 'فلٹرز دکھائیں',
                'Hide Filters': 'فلٹرز چھپائیں',
                'Switch Shop': 'شاپ تبدیل کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view financial summary': 'براہ کرم مالی خلاصہ دیکھنے کے لیے شاپ منتخب کریں',
                'Loading data...': 'ڈیٹا لوڈ ہو رہا ہے...',
                'No data available': 'کوئی ڈیٹا دستیاب نہیں',
                'Profit & Loss': 'نفع و نقصان',
                'Financial Overview': 'مالی جائزہ',
                'Total Revenue': 'کل آمدنی',
                'Total Cost': 'کل لاگت',
                'Net Income': 'خالص آمدنی',
                'Margin': 'مارجن',
                'All': 'سب',
                'Today': 'آج',
                'This Week': 'اس ہفتے',
                'This Month': 'اس مہینے',
                'This Year': 'اس سال',
                'Custom': 'اپنی مرضی',
                'Apply': 'لگائیں',
                'Cancel': 'منسوخ کریں',
                'Close': 'بند کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Report Generated': 'رپورٹ تیار ہو گئی',
                'PDF Generation Error': 'پی ڈی ایف بنانے میں خرابی',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'A4': 'A4',
                'Letter': 'لیٹر',
                'Legal': 'لیگل',
                'A5': 'A5',
                'Orientation': 'اورینٹیشن',
                'Page Size': 'پیج سائز',
                'Financial Report': 'مالی رپورٹ',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'Page': 'صفحہ',
                'of': 'میں سے',
                'No records found': 'کوئی ریکارڈ نہیں ملا',
                'Amount': 'رقم',
                'Status': 'حالت',
                'Date': 'تاریخ',
                'Description': 'تفصیل',
                'Type': 'قسم',
                'Reference': 'حوالہ',
                'Business Name': 'کاروباری نام',
                'Address': 'پتہ',
                'Phone': 'فون',
                'Email': 'ای میل',
                'Product Business': 'پروڈکٹ کا کاروبار',
                'Service Business': 'سروس کا کاروبار',
                'Mobchange Business': 'موبائل تبدیلی کا کاروبار',
                'Total Revenue': 'کل آمدنی',
                'Total COGS': 'کل لاگت',
                'Net Profit': 'خالص نفع',
                'Business Type': 'کاروبار کی قسم',
                'Cost': 'لاگت',
                'Profit': 'نفع',
                'Revenue Stream': 'آمدنی کا ذریعہ',
                'Direct Costs': 'براہ راست اخراجات',
                'Service Employee Cost': 'سروس ملازم کی لاگت',
                'Parts Used': 'استعمال شدہ پرزے',
            },
            'ar': {
                'Financial Summary': 'الملخص المالي',
                'Profit & Loss Statement': 'بيان الربح والخسارة',
                'All financial details are below': 'جميع التفاصيل المالية أدناه',
                'Date Range': 'نطاق التاريخ',
                'Start Date': 'تاريخ البداية',
                'End Date': 'تاريخ النهاية',
                'Apply Filter': 'تطبيق الفلتر',
                'Reset': 'إعادة تعيين',
                'Product Sales Revenue': 'إيرادات مبيعات المنتجات',
                'Product Sales': 'مبيعات المنتجات',
                'Cost of Goods Sold': 'تكلفة البضائع المباعة',
                'Product Returns': 'مرتجعات المنتجات',
                'Service Revenue': 'إيرادات الخدمات',
                'Service Employee Payments': 'مدفوعات موظفي الخدمات',
                'Service Profit': 'ربح الخدمة',
                'Mobchange Revenue': 'إيرادات تغيير الجوال',
                'Mobchange Parts Cost': 'تكلفة قطع غيار تغيير الجوال',
                'Mobchange Profit': 'ربح تغيير الجوال',
                'General Expenses': 'المصروفات العامة',
                'Employee Salaries': 'رواتب الموظفين',
                'Gross Profit': 'الربح الإجمالي',
                'Operating Profit': 'الربح التشغيلي',
                'Net Profit': 'صافي الربح',
                'Revenue': 'الإيرادات',
                'Operating Expenses': 'المصروفات التشغيلية',
                'Sales': 'المبيعات',
                'Purchases': 'المشتريات',
                'Services': 'الخدمات',
                'Mobchange': 'تغيير الجوال',
                'Returns': 'المرتجعات',
                'Salaries': 'الرواتب',
                'Expenses': 'المصروفات',
                'Total': 'الإجمالي',
                'Search...': 'بحث...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'Filter': 'فلتر',
                'Filters': 'المرشحات',
                'Show Filters': 'إظهار المرشحات',
                'Hide Filters': 'إخفاء المرشحات',
                'Switch Shop': 'تبديل المتجر',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view financial summary': 'الرجاء اختيار متجر لعرض الملخص المالي',
                'Loading data...': 'جاري تحميل البيانات...',
                'No data available': 'لا توجد بيانات متاحة',
                'Profit & Loss': 'الربح والخسارة',
                'Financial Overview': 'نظرة عامة مالية',
                'Total Revenue': 'إجمالي الإيرادات',
                'Total Cost': 'إجمالي التكلفة',
                'Net Income': 'صافي الدخل',
                'Margin': 'الهامش',
                'All': 'الكل',
                'Today': 'اليوم',
                'This Week': 'هذا الأسبوع',
                'This Month': 'هذا الشهر',
                'This Year': 'هذه السنة',
                'Custom': 'مخصص',
                'Apply': 'تطبيق',
                'Cancel': 'إلغاء',
                'Close': 'إغلاق',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Report Generated': 'تم إنشاء التقرير',
                'PDF Generation Error': 'خطأ في إنشاء PDF',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'A4': 'A4',
                'Letter': 'رسالة',
                'Legal': 'قانوني',
                'A5': 'A5',
                'Orientation': 'الاتجاه',
                'Page Size': 'حجم الصفحة',
                'Financial Report': 'التقرير المالي',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'Page': 'صفحة',
                'of': 'من',
                'No records found': 'لم يتم العثور على سجلات',
                'Amount': 'المبلغ',
                'Status': 'الحالة',
                'Date': 'التاريخ',
                'Description': 'الوصف',
                'Type': 'النوع',
                'Reference': 'المرجع',
                'Business Name': 'اسم العمل',
                'Address': 'العنوان',
                'Phone': 'الهاتف',
                'Email': 'البريد الإلكتروني',
                'Product Business': 'تجارة المنتجات',
                'Service Business': 'تجارة الخدمات',
                'Mobchange Business': 'تجارة تغيير الجوال',
                'Total Revenue': 'إجمالي الإيرادات',
                'Total COGS': 'إجمالي التكلفة',
                'Net Profit': 'صافي الربح',
                'Business Type': 'نوع العمل',
                'Cost': 'التكلفة',
                'Profit': 'الربح',
                'Revenue Stream': 'مصدر الإيرادات',
                'Direct Costs': 'التكاليف المباشرة',
                'Service Employee Cost': 'تكلفة موظف الخدمة',
                'Parts Used': 'القطع المستخدمة',
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        financialSummary: 'Financial Summary',
        profitLossStatement: 'Profit & Loss Statement',
        allDetailsBelow: 'All financial details are below',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilter: 'Apply Filter',
        reset: 'Reset',
        productSalesRevenue: 'Product Sales Revenue',
        productSales: 'Product Sales',
        costOfGoodsSold: 'Cost of Goods Sold',
        productReturns: 'Product Returns',
        serviceRevenue: 'Service Revenue',
        serviceEmployeePayments: 'Service Employee Payments',
        serviceProfit: 'Service Profit',
        mobchangeRevenue: 'Mobchange Revenue',
        mobchangePartsCost: 'Mobchange Parts Cost',
        mobchangeProfit: 'Mobchange Profit',
        generalExpenses: 'General Expenses',
        employeeSalaries: 'Employee Salaries',
        grossProfit: 'Gross Profit',
        operatingProfit: 'Operating Profit',
        netProfit: 'Net Profit',
        revenue: 'Revenue',
        operatingExpenses: 'Operating Expenses',
        sales: 'Sales',
        purchases: 'Purchases',
        services: 'Services',
        mobchange: 'Mobchange',
        returns: 'Returns',
        salaries: 'Salaries',
        expenses: 'Expenses',
        total: 'Total',
        search: 'Search...',
        refresh: 'Refresh',
        exportPDF: 'Export PDF',
        filter: 'Filter',
        filters: 'Filters',
        showFilters: 'Show Filters',
        hideFilters: 'Hide Filters',
        switchShop: 'Switch Shop',
        noShopSelected: 'No shop selected',
        selectShopMessage: 'Please select a shop to view financial summary',
        loadingData: 'Loading data...',
        noDataAvailable: 'No data available',
        profitAndLoss: 'Profit & Loss',
        financialOverview: 'Financial Overview',
        totalRevenue: 'Total Revenue',
        totalCost: 'Total Cost',
        netIncome: 'Net Income',
        margin: 'Margin',
        all: 'All',
        today: 'Today',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        thisYear: 'This Year',
        custom: 'Custom',
        apply: 'Apply',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        reportGenerated: 'Report Generated',
        pdfGenerationError: 'PDF Generation Error',
        portrait: 'Portrait',
        landscape: 'Landscape',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        orientation: 'Orientation',
        pageSize: 'Page Size',
        financialReport: 'Financial Report',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        page: 'Page',
        of: 'of',
        noRecordsFound: 'No records found',
        amount: 'Amount',
        status: 'Status',
        date: 'Date',
        description: 'Description',
        type: 'Type',
        reference: 'Reference',
        businessName: 'Business Name',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        productBusiness: 'Product Business',
        serviceBusiness: 'Service Business',
        mobchangeBusiness: 'Mobchange Business',
        totalRevenue: 'Total Revenue',
        totalCOGS: 'Total COGS',
        netProfit: 'Net Profit',
        businessType: 'Business Type',
        cost: 'Cost',
        profit: 'Profit',
        revenueStream: 'Revenue Stream',
        directCosts: 'Direct Costs',
        serviceEmployeeCost: 'Service Employee Cost',
        partsUsed: 'Parts Used',
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const financialTranslator = createFinancialTranslator();

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozsumui-toast ${toast.type}`}>
            <div className="ozsumui-toast-content">
                <div className="ozsumui-toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozsumui-toast-text">
                    <div className="ozsumui-toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozsumui-toast-message">{toast.message}</div>
                </div>
                <button className="ozsumui-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== SHOP SELECTOR MODAL ====================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozsumui-modal-overlay" onClick={onClose}>
            <div className="ozsumui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozsumui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozsumui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozsumui-modal-body">
                    {loading ? (
                        <div className="ozsumui-loading-shops">
                            <div className="ozsumui-spinner-small"></div>
                            <p>Loading shops...</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozsumui-no-shops">
                            <p>No shops available</p>
                        </div>
                    ) : (
                        <div className="ozsumui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozsumui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozsumui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozsumui-shop-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozsumui-shop-details">
                                        <div className="ozsumui-shop-name">{shop.shop_name}</div>
                                        <div className="ozsumui-shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozsumui-shop-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>Current</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==================== CUSTOM DROPDOWN ====================
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        setSearchTerm(displayValue);
    }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="ozsumui-dropdown-wrapper" ref={wrapperRef}>
            <div className="ozsumui-input-wrapper">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!options.find(opt => opt.label.toLowerCase() === e.target.value.toLowerCase())) {
                            onChange('');
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozsumui-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozsumui-dropdown-menu">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`ozsumui-dropdown-option ${value === opt.value ? 'ozsumui-selected' : ''}`}
                                onClick={() => handleSelect(opt)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozsumui-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==================== MAIN COMPONENT ====================
const FinancialSummary = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Business Info State
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        bussinesslogo_url: '',
        businessaddress: '',
        phonenumber: '',
        email: '',
        fullname: '',
        shop_name: '',
        shop_code: '',
        shop_id: ''
    });

    // Financial Data State - from the profit/loss endpoint
    const [profitLossData, setProfitLossData] = useState({
        date_range: { start_date: '', end_date: '' },
        product_sales: { revenue: 0, cogs: 0, profit: 0, returns_loss: 0, profit_after_returns: 0 },
        service: { revenue: 0, employee_payments: 0, profit: 0 },
        mobchange: { profit: 0 },
        expenses: { general_expenses: 0, general_salaries: 0, total_operating_expenses: 0 },
        gross_profit: 0,
        net_profit: 0
    });

    // Derived summary
    const summary = useMemo(() => {
        const d = profitLossData;
        const productSales = d.product_sales?.revenue || 0;
        const costOfGoodsSold = d.product_sales?.cogs || 0;
        const productReturns = d.product_sales?.returns_loss || 0;
        const productProfit = d.product_sales?.profit_after_returns || 0;
        const serviceRevenue = d.service?.revenue || 0;
        const serviceEmployeePayments = d.service?.employee_payments || 0;
        const serviceProfit = d.service?.profit || 0;
        const mobchangeProfit = d.mobchange?.profit || 0;
        const totalRevenue = productSales + serviceRevenue;
        const totalDirectCosts = costOfGoodsSold + productReturns + serviceEmployeePayments;
        const grossProfit = d.gross_profit || 0;
        const generalExpenses = d.expenses?.general_expenses || 0;
        const employeeSalaries = d.expenses?.general_salaries || 0;
        const totalOperatingExpenses = d.expenses?.total_operating_expenses || 0;
        const operatingProfit = grossProfit - totalOperatingExpenses;
        const netProfit = d.net_profit || 0;

        return {
            productSales,
            costOfGoodsSold,
            productReturns,
            productProfit,
            serviceRevenue,
            serviceEmployeePayments,
            serviceProfit,
            mobchangeProfit,
            totalRevenue,
            totalDirectCosts,
            grossProfit,
            generalExpenses,
            employeeSalaries,
            totalOperatingExpenses,
            operatingProfit,
            netProfit
        };
    }, [profitLossData]);

    // Date Filter State
    const [dateRange, setDateRange] = useState('thisMonth');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // PDF Options
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');

    // Theme, Dark Mode, Language
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // Date Range Options
    const dateRangeOptions = useMemo(() => [
        { value: 'today', label: translations.today || 'Today' },
        { value: 'thisWeek', label: translations.thisWeek || 'This Week' },
        { value: 'thisMonth', label: translations.thisMonth || 'This Month' },
        { value: 'thisYear', label: translations.thisYear || 'This Year' },
        { value: 'custom', label: translations.custom || 'Custom' }
    ], [translations]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    // ============================================
    // THEME SYNC
    // ============================================
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
            document.documentElement.style.setProperty('--ozsumui-primary', color);
            document.documentElement.style.setProperty('--ozsumui-primary-dark', color);
            document.documentElement.style.setProperty('--ozsumui-primary-light', color);
            setCurrentTheme(color);
        };

        applyTheme(currentTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [currentTheme]);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);

            const container = document.querySelector('.ozsumui-container');
            if (container) {
                if (isDark) {
                    container.classList.add('ozsumui-dark');
                } else {
                    container.classList.remove('ozsumui-dark');
                }
            }

            if (isDark) {
                document.body.classList.add('ozsumui-dark');
                document.documentElement.classList.add('ozsumui-dark');
            } else {
                document.body.classList.remove('ozsumui-dark');
                document.documentElement.classList.remove('ozsumui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozsumui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozsumui-dark');
                    } else {
                        container.classList.remove('ozsumui-dark');
                    }
                }
                if (isDark) {
                    document.body.classList.add('ozsumui-dark');
                    document.documentElement.classList.add('ozsumui-dark');
                } else {
                    document.body.classList.remove('ozsumui-dark');
                    document.documentElement.classList.remove('ozsumui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await financialTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozsumui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozsumui-rtl');
                container.classList.remove('ozsumui-ltr');
            } else {
                container.classList.add('ozsumui-ltr');
                container.classList.remove('ozsumui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ============================================
    // GET DATE RANGE
    // ============================================
    const getDateRange = useCallback((range, customStart, customEnd) => {
        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (range) {
            case 'today':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                break;
            case 'thisWeek':
                const dayOfWeek = now.getDay();
                start = new Date(now);
                start.setDate(now.getDate() - dayOfWeek);
                start.setHours(0, 0, 0, 0);
                end = new Date(now);
                end.setHours(23, 59, 59, 999);
                break;
            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
                break;
            case 'thisYear':
                start = new Date(now.getFullYear(), 0, 1);
                end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
                break;
            case 'custom':
                if (customStart && customEnd) {
                    start = new Date(customStart);
                    start.setHours(0, 0, 0, 0);
                    end = new Date(customEnd);
                    end.setHours(23, 59, 59, 999);
                } else {
                    start = new Date(now.getFullYear(), now.getMonth(), 1);
                    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
                }
                break;
            default:
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        }

        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        };
    }, []);

    // ============================================
    // FETCH PROFIT & LOSS DATA
    // ============================================
    const fetchProfitLoss = useCallback(async () => {
        if (!user || !currentShop) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const shopId = currentShop.shop_id;
            const username = user.username;
            setAuthData(user, currentShop);

            // Get date range
            let dates;
            if (dateRange === 'custom') {
                dates = getDateRange('custom', customStartDate, customEndDate);
            } else {
                dates = getDateRange(dateRange);
            }

            const payload = {
                action: 'get_profit_loss',
                shop_id: shopId,
                username: username,
                start_date: dates.startDate,
                end_date: dates.endDate
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_finance.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (data.status === 'success') {
                setProfitLossData(data.data);
                setStartDate(data.data.date_range.start_date);
                setEndDate(data.data.date_range.end_date);
                showToast('success', 'Financial data updated successfully');
            } else {
                throw new Error(data.message || 'Failed to fetch profit/loss data');
            }
        } catch (error) {
            console.error('Error fetching profit/loss:', error);
            showToast('error', error.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    }, [user, currentShop, dateRange, customStartDate, customEndDate, getDateRange, showToast]);

    // ============================================
    // FETCH BUSINESS INFO
    // ============================================
    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo_url || '',
                        businessaddress: data.businessaddress || '',
                        phonenumber: data.phonenumber || '',
                        email: data.email || '',
                        fullname: data.fullname || '',
                        shop_name: data.shop_name || currentShop?.shop_name || '',
                        shop_code: data.shop_code || currentShop?.shop_code || '',
                        shop_id: data.shop_id || currentShop?.shop_id || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || '',
                        bussinesslogo: '',
                        bussinesslogo_url: '',
                        businessaddress: user.business_address || '',
                        phonenumber: user.phonenumber || '',
                        email: user.email || '',
                        fullname: user.fullname || '',
                        shop_name: currentShop?.shop_name || '',
                        shop_code: currentShop?.shop_code || '',
                        shop_id: currentShop?.shop_id || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: '',
                    bussinesslogo_url: '',
                    businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '',
                    email: user.email || '',
                    fullname: user.fullname || '',
                    shop_name: currentShop?.shop_name || '',
                    shop_code: currentShop?.shop_code || '',
                    shop_id: currentShop?.shop_id || ''
                });
            }
        }
    }, [user, currentShop]);

    // ============================================
    // FETCH SHOPS
    // ============================================
    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    // ============================================
    // LOAD DATA ON MOUNT AND FILTER CHANGE
    // ============================================
    useEffect(() => {
        if (user && currentShop) {
            fetchProfitLoss();
            fetchBusinessInfo();
            fetchAvailableShops();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, currentShop, dateRange, customStartDate, customEndDate]);

    // ============================================
    // HANDLE SHOP SWITCH
    // ============================================
    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setShowShopSelector(false);
                fetchProfitLoss();
                fetchBusinessInfo();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, fetchProfitLoss, fetchBusinessInfo]);

    // ============================================
    // FORMAT CURRENCY
    // ============================================
    const formatCurrency = (value) => {
        const num = parseFloat(value);
        if (isNaN(num) || num === null || num === undefined) {
            return '₨0';
        }
        return new Intl.NumberFormat('en-PK', {
            style: 'currency',
            currency: 'PKR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num).replace('PKR', '₨');
    };

    // ============================================
    // FORMAT DATE
    // ============================================
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    };

    // ============================================
    // GET TODAY DATE
    // ============================================
    const getTodayDate = () => new Date().toISOString().split('T')[0];

    // ============================================
    // ESCAPE HTML
    // ============================================
    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>"]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            if (m === '"') return '&quot;';
            return m;
        });
    };

    // ============================================
    // EXPORT TO PDF
    // ============================================
    const exportToPDF = useCallback(async () => {
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = currentLang === 'ur' || currentLang === 'ar';
            const pageFormat = pdfPageSize || 'a4';

            const pdf = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();

            const container = document.createElement('div');
            container.style.width = '750px';
            container.style.padding = '20px';
            container.style.backgroundColor = '#ffffff';
            container.style.color = '#1f2937';
            container.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
            container.style.direction = isRTL ? 'rtl' : 'ltr';
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';

            const getTranslation = (key, fallback = '') => translations[key] || fallback;

            const profitLossColor = summary.netProfit >= 0 ? '#10b981' : '#ef4444';
            const profitLossLabel = summary.netProfit >= 0 ? (translations.profit || 'Profit') : (translations.loss || 'Loss');

            let logoHtml = '';
            const logoUrl = businessInfo.bussinesslogo_url || businessInfo.bussinesslogo || '';
            
            if (logoUrl && (logoUrl.startsWith('data:image') || logoUrl.startsWith('http'))) {
                logoHtml = `
                    <div style="flex-shrink: 0; margin-${isRTL ? 'left' : 'right'}: 12px;">
                        <img src="${logoUrl}" alt="${escapeHtml(businessInfo.businessname || 'Business')}" 
                             style="max-height: 60px; max-width: 80px; width: auto; height: auto; object-fit: contain; display: block;" 
                             onerror="this.style.display='none'" />
                    </div>
                `;
            }

            let html = `
                <div style="display: flex; align-items: center; justify-content: space-between; padding-bottom: 12px; border-bottom: 2px solid #e5e7eb; margin-bottom: 15px;">
                    <div style="display: flex; align-items: center; flex: 1;">
                        ${logoHtml}
                        <div style="flex: 1; min-width: 0;">
                            <div style="font-size: 18px; font-weight: 700; color: #1f2937; letter-spacing: 0.5px;">
                                ${escapeHtml(businessInfo.businessname || currentShop?.shop_name || 'Financial Report')}
                            </div>
                            ${businessInfo.businessaddress ? `
                                <div style="font-size: 8px; color: #6b7280; margin-top: 2px; display: flex; align-items: center; gap: 4px;">
                                    <span>📍</span> ${escapeHtml(businessInfo.businessaddress)}
                                </div>
                            ` : ''}
                            <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 2px; font-size: 8px; color: #6b7280;">
                                ${businessInfo.phonenumber ? `
                                    <span style="display: flex; align-items: center; gap: 3px;">
                                        <span>📞</span> ${escapeHtml(businessInfo.phonenumber)}
                                    </span>
                                ` : ''}
                                ${businessInfo.email ? `
                                    <span style="display: flex; align-items: center; gap: 3px;">
                                        <span>✉️</span> ${escapeHtml(businessInfo.email)}
                                    </span>
                                ` : ''}
                                ${businessInfo.shop_code ? `
                                    <span style="display: flex; align-items: center; gap: 3px;">
                                        <span>🏪</span> ${escapeHtml(businessInfo.shop_code)}
                                    </span>
                                ` : ''}
                            </div>
                        </div>
                    </div>
                    <div style="text-align: ${isRTL ? 'left' : 'right'}; flex-shrink: 0; margin-${isRTL ? 'right' : 'left'}: 15px;">
                        <div style="font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: #1f2937;">
                            ${getTranslation('financialReport', 'Financial Report')}
                        </div>
                        <div style="font-size: 7px; color: #6b7280; margin-top: 2px;">
                            ${getTranslation('generated', 'Generated')}: ${new Date().toLocaleString(currentLang)}
                        </div>
                        <div style="font-size: 7px; color: #6b7280;">
                            ${getTranslation('dateRange', 'Date Range')}: ${startDate} ${getTranslation('to', 'to')} ${endDate}
                        </div>
                    </div>
                </div>

                <!-- PRODUCT BUSINESS -->
                <div style="margin-bottom: 10px;">
                    <h3 style="font-size: 10px; color: #3b82f6; margin: 0 0 4px 0; border-bottom: 1px solid #3b82f640; padding-bottom: 4px;">
                        📦 ${getTranslation('productBusiness', 'Product Business')}
                    </h3>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 4px; font-size: 8px;">
                        <div><span style="color: #6b7280;">${getTranslation('productSales', 'Product Sales')}:</span> <strong>${formatCurrency(summary.productSales)}</strong></div>
                        <div><span style="color: #6b7280;">${getTranslation('costOfGoodsSold', 'COGS')}:</span> <strong style="color: #ef4444;">${formatCurrency(summary.costOfGoodsSold)}</strong></div>
                        <div><span style="color: #6b7280;">${getTranslation('productReturns', 'Returns')}:</span> <strong style="color: #f59e0b;">${formatCurrency(summary.productReturns)}</strong></div>
                        <div><span style="color: #6b7280;">${getTranslation('profit', 'Profit')}:</span> <strong style="color: ${summary.productProfit >= 0 ? '#22c55e' : '#ef4444'};">${formatCurrency(summary.productProfit)}</strong></div>
                    </div>
                </div>

                <!-- SERVICE BUSINESS -->
                <div style="margin-bottom: 10px;">
                    <h3 style="font-size: 10px; color: #8b5cf6; margin: 0 0 4px 0; border-bottom: 1px solid #8b5cf640; padding-bottom: 4px;">
                        🔧 ${getTranslation('serviceBusiness', 'Service Business')}
                    </h3>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; font-size: 8px;">
                        <div><span style="color: #6b7280;">${getTranslation('serviceRevenue', 'Service Revenue')}:</span> <strong>${formatCurrency(summary.serviceRevenue)}</strong></div>
                        <div><span style="color: #6b7280;">${getTranslation('serviceEmployeeCost', 'Service Employee Cost')}:</span> <strong style="color: #ef4444;">${formatCurrency(summary.serviceEmployeePayments)}</strong></div>
                        <div><span style="color: #6b7280;">${getTranslation('profit', 'Profit')}:</span> <strong style="color: ${summary.serviceProfit >= 0 ? '#22c55e' : '#ef4444'};">${formatCurrency(summary.serviceProfit)}</strong></div>
                    </div>
                </div>

                <!-- MOBCHANGE BUSINESS -->
                <div style="margin-bottom: 10px;">
                    <h3 style="font-size: 10px; color: #f59e0b; margin: 0 0 4px 0; border-bottom: 1px solid #f59e0b40; padding-bottom: 4px;">
                        📱 ${getTranslation('mobchangeBusiness', 'Mobchange Business')}
                    </h3>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; font-size: 8px;">
                        <div><span style="color: #6b7280;">${getTranslation('mobchangeProfit', 'Mobchange Profit')}:</span> <strong style="color: ${summary.mobchangeProfit >= 0 ? '#22c55e' : '#ef4444'};">${formatCurrency(summary.mobchangeProfit)}</strong></div>
                    </div>
                </div>

                <!-- SUMMARY TOTALS -->
                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; margin: 10px 0; padding: 8px 10px; background: #f8fafc; border: 1px solid #e5e7eb;">
                    <div style="text-align: center; font-size: 8px;">
                        <div style="color: #6b7280;">${getTranslation('totalRevenue', 'Total Revenue')}</div>
                        <div style="font-weight: 700; color: #22c55e;">${formatCurrency(summary.totalRevenue)}</div>
                    </div>
                    <div style="text-align: center; font-size: 8px;">
                        <div style="color: #6b7280;">${getTranslation('totalCOGS', 'Total Direct Costs')}</div>
                        <div style="font-weight: 700; color: #ef4444;">${formatCurrency(summary.totalDirectCosts)}</div>
                    </div>
                    <div style="text-align: center; font-size: 8px;">
                        <div style="color: #6b7280;">${getTranslation('grossProfit', 'Gross Profit')}</div>
                        <div style="font-weight: 700; color: ${summary.grossProfit >= 0 ? '#22c55e' : '#ef4444'};">${formatCurrency(summary.grossProfit)}</div>
                    </div>
                </div>

                <!-- OPERATING EXPENSES -->
                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; margin: 6px 0; padding: 6px 8px; background: #fef2f2; border: 1px solid #fecaca;">
                    <div style="text-align: center; font-size: 7px;">
                        <div style="color: #6b7280;">${getTranslation('generalExpenses', 'General Expenses')}</div>
                        <div style="font-weight: 700; color: #ef4444;">${formatCurrency(summary.generalExpenses)}</div>
                    </div>
                    <div style="text-align: center; font-size: 7px;">
                        <div style="color: #6b7280;">${getTranslation('employeeSalaries', 'Employee Salaries')}</div>
                        <div style="font-weight: 700; color: #ef4444;">${formatCurrency(summary.employeeSalaries)}</div>
                    </div>
                    <div style="text-align: center; font-size: 7px;">
                        <div style="color: #6b7280;">${getTranslation('totalOperatingExpenses', 'Total Operating Expenses')}</div>
                        <div style="font-weight: 700; color: #ef4444;">${formatCurrency(summary.totalOperatingExpenses)}</div>
                    </div>
                </div>

                <!-- NET PROFIT -->
                <div style="background: ${profitLossColor}15; border: 2px solid ${profitLossColor}; padding: 12px; text-align: center; margin: 8px 0;">
                    <div style="font-size: 8px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px;">${getTranslation('netProfit', 'Net Profit')}</div>
                    <div style="font-size: 20px; font-weight: 700; color: ${profitLossColor};">
                        ${formatCurrency(summary.netProfit)} <span style="font-size: 11px;">(${profitLossLabel})</span>
                    </div>
                    <div style="display: flex; justify-content: center; gap: 16px; font-size: 7px; color: #6b7280; margin-top: 3px;">
                        <span>${getTranslation('operatingProfit', 'Operating Profit')}: <strong style="color: ${summary.operatingProfit >= 0 ? '#22c55e' : '#ef4444'};">${formatCurrency(summary.operatingProfit)}</strong></span>
                    </div>
                </div>

                <div style="border-top: 1px solid #e5e7eb; padding-top: 6px; text-align: center; font-size: 6px; color: #9ca3af;">
                    ${getTranslation('generatedBy', 'Generated by')}: ${escapeHtml(user?.fullname || user?.username || 'System')} | 
                    ${getTranslation('date', 'Date')}: ${new Date().toLocaleString(currentLang)} |
                    ${getTranslation('shopCode', 'Shop Code')}: ${escapeHtml(currentShop?.shop_code || 'N/A')}
                </div>
            `;

            container.innerHTML = html;
            document.body.appendChild(container);

            const canvas = await html2canvas(container, {
                scale: 2,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true
            });

            document.body.removeChild(container);

            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);

            const fileName = `Financial_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${getTodayDate()}.pdf`;
            pdf.save(fileName);
            showToast('success', translations.reportGenerated || 'Report generated successfully');

        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [currentLanguage, pdfOrientation, pdfPageSize, translations, currentShop, user, showToast, summary, startDate, endDate, formatCurrency, businessInfo]);

    // ============================================
    // RENDER
    // ============================================
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozsumui-container ${isRTL ? 'ozsumui-rtl' : 'ozsumui-ltr'} ${darkMode ? 'ozsumui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Sticky Header */}
            <div className="ozsumui-header">
                <div className="ozsumui-header-bg">
                    <div className="ozsumui-orb orb-1"></div>
                    <div className="ozsumui-orb orb-2"></div>
                    <div className="ozsumui-orb orb-3"></div>
                    <div className="ozsumui-orb orb-4"></div>
                    <div className="ozsumui-orb orb-5"></div>
                </div>
                <div className="ozsumui-header-content">
                    <div className="ozsumui-header-left">
                        <div className="ozsumui-header-title">
                            <h1><FontAwesomeIcon icon={faChartLine} /> {translations.financialSummary || 'Financial Summary'}</h1>
                            <p>{translations.profitLossStatement || 'Profit & Loss Statement'}</p>
                        </div>
                    </div>
                    <div className="ozsumui-header-right">
                        <button
                            className="ozsumui-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? (translations.hideFilters || "Hide Filters") : (translations.showFilters || "Show Filters")}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? (translations.hideFilters || "Hide Filters") : (translations.filters || "Filters")}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozsumui-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            {showFilters && (
                <div className="ozsumui-filters">
                    <div className="ozsumui-filters-scroll">
                        <div className="ozsumui-filters-row">
                            <div className="ozsumui-filter-group">
                                <CustomDropdown
                                    label={translations.dateRange || 'Date Range'}
                                    icon={faCalendar}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    options={dateRangeOptions}
                                    translations={translations}
                                />
                            </div>

                            {dateRange === 'custom' && (
                                <>
                                    <div className="ozsumui-filter-group ozsumui-date-filter">
                                        <div className="ozsumui-floating-wrapper">
                                            <input
                                                type="date"
                                                id="customStartDate"
                                                value={customStartDate}
                                                onChange={(e) => setCustomStartDate(e.target.value)}
                                                max={getTodayDate()}
                                                placeholder=" "
                                            />
                                            <label htmlFor="customStartDate">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {translations.startDate || 'Start Date'}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="ozsumui-filter-group ozsumui-date-filter">
                                        <div className="ozsumui-floating-wrapper">
                                            <input
                                                type="date"
                                                id="customEndDate"
                                                value={customEndDate}
                                                onChange={(e) => setCustomEndDate(e.target.value)}
                                                min={customStartDate}
                                                max={getTodayDate()}
                                                placeholder=" "
                                            />
                                            <label htmlFor="customEndDate">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {translations.endDate || 'End Date'}
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="ozsumui-filter-group">
                                <button onClick={fetchProfitLoss} className="ozsumui-refresh-btn" disabled={loading}>
                                    <FontAwesomeIcon icon={faRefresh} />
                                    <span className="ozsumui-btn-text">{translations.refresh || 'Refresh'}</span>
                                </button>
                            </div>

                            <div className="ozsumui-filter-group">
                                <CustomDropdown
                                    label={translations.orientation || 'Orientation'}
                                    icon={faFilePdf}
                                    value={pdfOrientation}
                                    onChange={setPdfOrientation}
                                    options={pdfOrientationOptions}
                                    translations={translations}
                                />
                            </div>

                            <div className="ozsumui-filter-group">
                                <CustomDropdown
                                    label={translations.pageSize || 'Page Size'}
                                    icon={faFilePdf}
                                    value={pdfPageSize}
                                    onChange={setPdfPageSize}
                                    options={pdfPageSizeOptions}
                                    translations={translations}
                                />
                            </div>

                            <div className="ozsumui-filter-group">
                                <button
                                    onClick={exportToPDF}
                                    className="ozsumui-export-btn"
                                    disabled={loading || pdfLoading}
                                >
                                    {pdfLoading ? <div className="ozsumui-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                    <span className="ozsumui-btn-text">{translations.exportPDF || 'Export PDF'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="ozsumui-loading">
                    <div className="ozsumui-spinner"></div>
                    <p>{translations.loadingData || 'Loading data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozsumui-no-shop">
                    <div className="ozsumui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShopMessage || 'Please select a shop to view financial summary'}</p>
                        <button className="ozsumui-btn ozsumui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="ozsumui-cards">
                        <div className="ozsumui-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                            <div className="ozsumui-card-icon" style={{ background: '#3b82f620', color: '#3b82f6' }}>
                                <FontAwesomeIcon icon={faShoppingCart} />
                            </div>
                            <div className="ozsumui-card-info">
                                <h3>{formatCurrency(summary.productSales)}</h3>
                                <p>{translations.productSales || 'Product Sales'}</p>
                            </div>
                        </div>

                        <div className="ozsumui-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                            <div className="ozsumui-card-icon" style={{ background: '#8b5cf620', color: '#8b5cf6' }}>
                                <FontAwesomeIcon icon={faWrench} />
                            </div>
                            <div className="ozsumui-card-info">
                                <h3>{formatCurrency(summary.serviceRevenue)}</h3>
                                <p>{translations.serviceRevenue || 'Service Revenue'}</p>
                            </div>
                        </div>

                        <div className="ozsumui-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                            <div className="ozsumui-card-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                            </div>
                            <div className="ozsumui-card-info">
                                <h3>{formatCurrency(summary.mobchangeProfit)}</h3>
                                <p>{translations.mobchangeProfit || 'Mobchange Profit'}</p>
                            </div>
                        </div>

                        <div className="ozsumui-card" style={{ borderLeft: `4px solid ${summary.netProfit >= 0 ? '#22c55e' : '#ef4444'}` }}>
                            <div className="ozsumui-card-icon" style={{ background: summary.netProfit >= 0 ? '#22c55e20' : '#ef444420', color: summary.netProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                                <FontAwesomeIcon icon={faCoins} />
                            </div>
                            <div className="ozsumui-card-info">
                                <h3 style={{ color: summary.netProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                                    {formatCurrency(summary.netProfit)}
                                    <span style={{ fontSize: '0.6rem', fontWeight: '400', marginLeft: '4px' }}>
                                        ({summary.netProfit >= 0 ? translations.profit || 'Profit' : translations.loss || 'Loss'})
                                    </span>
                                </h3>
                                <p>{translations.netProfit || 'Net Profit'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Profit Breakdown Table */}
                    <div className="ozsumui-table-wrap">
                        <div className="ozsumui-table-container">
                            <div className="ozsumui-table-scroll">
                                <table className="ozsumui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozsumui-center" style={{ width: '25%' }}>{translations.businessType || 'Business Type'}</th>
                                            <th className="ozsumui-center" style={{ width: '25%' }}>{translations.revenue || 'Revenue'}</th>
                                            <th className="ozsumui-center" style={{ width: '25%' }}>{translations.directCosts || 'Direct Costs'}</th>
                                            <th className="ozsumui-center" style={{ width: '25%' }}>{translations.profit || 'Profit'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="ozsumui-center"><strong style={{ color: '#3b82f6' }}>📦 {translations.productBusiness || 'Product'}</strong></td>
                                            <td className="ozsumui-center ozsumui-green">{formatCurrency(summary.productSales)}</td>
                                            <td className="ozsumui-center ozsumui-red">{formatCurrency(summary.costOfGoodsSold + summary.productReturns)}</td>
                                            <td className="ozsumui-center" style={{ color: summary.productProfit >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600' }}>{formatCurrency(summary.productProfit)}</td>
                                        </tr>
                                        <tr>
                                            <td className="ozsumui-center"><strong style={{ color: '#8b5cf6' }}>🔧 {translations.serviceBusiness || 'Service'}</strong></td>
                                            <td className="ozsumui-center ozsumui-green">{formatCurrency(summary.serviceRevenue)}</td>
                                            <td className="ozsumui-center ozsumui-red">{formatCurrency(summary.serviceEmployeePayments)}</td>
                                            <td className="ozsumui-center" style={{ color: summary.serviceProfit >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600' }}>{formatCurrency(summary.serviceProfit)}</td>
                                        </tr>
                                        <tr>
                                            <td className="ozsumui-center"><strong style={{ color: '#f59e0b' }}>📱 {translations.mobchangeBusiness || 'Mobchange'}</strong></td>
                                            <td className="ozsumui-center ozsumui-green">{formatCurrency(summary.mobchangeProfit)}</td>
                                            <td className="ozsumui-center ozsumui-red">-</td>
                                            <td className="ozsumui-center" style={{ color: summary.mobchangeProfit >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600' }}>{formatCurrency(summary.mobchangeProfit)}</td>
                                        </tr>
                                        <tr style={{ borderTop: '2px solid var(--ozsumui-border)' }}>
                                            <td className="ozsumui-center"><strong>{translations.total || 'Total'}</strong></td>
                                            <td className="ozsumui-center ozsumui-green"><strong>{formatCurrency(summary.totalRevenue)}</strong></td>
                                            <td className="ozsumui-center ozsumui-red"><strong>{formatCurrency(summary.totalDirectCosts)}</strong></td>
                                            <td className="ozsumui-center" style={{ color: summary.grossProfit >= 0 ? '#22c55e' : '#ef4444', fontWeight: '700' }}>{formatCurrency(summary.grossProfit)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Operating Expenses & Net Profit */}
                    <div className="ozsumui-pl" style={{ borderColor: summary.netProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                        <div className="ozsumui-pl-content">
                            <div className="ozsumui-pl-label">{translations.profitAndLoss || 'Profit & Loss Statement'}</div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', margin: '8px 0' }}>
                                <div style={{ textAlign: 'center', padding: '6px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                                    <div style={{ fontSize: '7px', color: '#6b7280', textTransform: 'uppercase' }}>{translations.generalExpenses || 'General Expenses'}</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>{formatCurrency(summary.generalExpenses)}</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '6px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                                    <div style={{ fontSize: '7px', color: '#6b7280', textTransform: 'uppercase' }}>{translations.employeeSalaries || 'Employee Salaries'}</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>{formatCurrency(summary.employeeSalaries)}</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '6px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                                    <div style={{ fontSize: '7px', color: '#6b7280', textTransform: 'uppercase' }}>{translations.totalOperatingExpenses || 'Total Operating Expenses'}</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>{formatCurrency(summary.totalOperatingExpenses)}</div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', margin: '8px 0' }}>
                                <div style={{ textAlign: 'center', padding: '8px', background: summary.operatingProfit >= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${summary.operatingProfit >= 0 ? '#bbf7d0' : '#fecaca'}` }}>
                                    <div style={{ fontSize: '7px', color: '#6b7280', textTransform: 'uppercase' }}>{translations.operatingProfit || 'Operating Profit'}</div>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: summary.operatingProfit >= 0 ? '#22c55e' : '#ef4444' }}>{formatCurrency(summary.operatingProfit)}</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: summary.netProfit >= 0 ? '#f0fdf4' : '#fef2f2', border: `2px solid ${summary.netProfit >= 0 ? '#22c55e' : '#ef4444'}` }}>
                                    <div style={{ fontSize: '7px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{translations.netProfit || 'Net Profit'}</div>
                                    <div style={{ fontSize: '20px', fontWeight: '700', color: summary.netProfit >= 0 ? '#22c55e' : '#ef4444' }}>
                                        {formatCurrency(summary.netProfit)}
                                        <span style={{ fontSize: '11px', fontWeight: '400', marginLeft: '6px' }}>
                                            ({summary.netProfit >= 0 ? translations.profit || 'Profit' : translations.loss || 'Loss'})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', fontSize: '9px', color: '#6b7280', flexWrap: 'wrap', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
                                <span>{translations.totalRevenue || 'Total Revenue'}: <strong style={{ color: '#22c55e' }}>{formatCurrency(summary.totalRevenue)}</strong></span>
                                <span>{translations.totalCOGS || 'Total Direct Costs'}: <strong style={{ color: '#ef4444' }}>{formatCurrency(summary.totalDirectCosts)}</strong></span>
                                <span>{translations.productReturns || 'Returns'}: <strong style={{ color: '#f59e0b' }}>{formatCurrency(summary.productReturns)}</strong></span>
                            </div>
                        </div>
                    </div>

                    {/* Footer Info */}
                    <div className="ozsumui-footer-info">
                        <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '6px' }} />
                        {translations.dateRange || 'Date Range'}: {startDate || '-'} {translations.to || 'to'} {endDate || '-'}
                    </div>
                </>
            )}
        </div>
    );
};

export default FinancialSummary;